/**
 * Onboarding Storage — localStorage utilities for tour progress
 */

import type { TourId } from './types';

const DEFAULT_PREFIX = 'thread-onboarding';

let storagePrefix = DEFAULT_PREFIX;

export function setStoragePrefix(prefix: string): void {
  storagePrefix = prefix;
}

export function getStoragePrefix(): string {
  return storagePrefix;
}

const getCompletedKey = () => `${storagePrefix}-completed`;
const getProgressKey = (tourId: TourId) => `${storagePrefix}-progress-${tourId}`;

export function markTourComplete(tourId: TourId): void {
  try {
    const completed = getCompletedTours();
    completed.add(tourId);
    localStorage.setItem(getCompletedKey(), JSON.stringify([...completed]));
  } catch {
    // localStorage not available
  }
}

export function hasCompletedTour(tourId: TourId): boolean {
  return getCompletedTours().has(tourId);
}

export function getCompletedTours(): Set<TourId> {
  try {
    const saved = localStorage.getItem(getCompletedKey());
    if (saved) {
      return new Set(JSON.parse(saved));
    }
  } catch {
    // localStorage not available
  }
  return new Set();
}

export function saveTourProgress(tourId: TourId, stepIndex: number): void {
  try {
    localStorage.setItem(getProgressKey(tourId), String(stepIndex));
  } catch {
    // localStorage not available
  }
}

export function getTourProgress(tourId: TourId): number {
  try {
    const saved = localStorage.getItem(getProgressKey(tourId));
    if (saved !== null) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available
  }
  return 0;
}

export function hasTourProgress(tourId: TourId): boolean {
  try {
    return localStorage.getItem(getProgressKey(tourId)) !== null;
  } catch {
    return false;
  }
}

export function clearTourProgress(tourId: TourId): void {
  try {
    localStorage.removeItem(getProgressKey(tourId));
  } catch {
    // localStorage not available
  }
}

export function resetOnboarding(): void {
  try {
    localStorage.removeItem(getCompletedKey());
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${storagePrefix}-progress-`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage not available
  }
}
